import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { toast } from 'react-toastify';
import { useActiveLeague } from '../hooks/useActiveLeague';
import { useGetUserLeagues } from '../api/leaguesApi';
import { useGetTeams } from '../api/teamsApi';
import {
  useGetNotifications,
  useGetUnreadCount,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useClearAllNotifications,
  useDeleteNotification
} from '../api/notificationsApi';
import NotificationService from '../services/notificationService';
import { timeDelta } from '../components/utils';
import { useCurrentTeamId } from '../hooks/useCurrentTeamId';
import CounterBidPopup from '../components/notifications/CounterBidPopup';
import { resolveNotificationNavigationPath } from '../utils/notificationNavigation';

import '../styles/notifications-inbox.css';

function NotificationsInboxPage() {
  const navigate = useNavigate();
  const { activeLeague } = useActiveLeague();
  const currentTeamId = useCurrentTeamId();
  const { data: userLeagues = [] } = useGetUserLeagues(true);
  const { data: myTeamsData = [] } = useGetTeams(true);
  const myTeams = useMemo(() => (
    Array.isArray(myTeamsData) ? myTeamsData : (myTeamsData ? [myTeamsData] : [])
  ), [myTeamsData]);

  // Filter state
  const [selectedLeague, setSelectedLeague] = useState(activeLeague?.slug || 'all');
  const [readFilter, setReadFilter] = useState('all'); // 'all' or 'unread'
  const [typeFilter, setTypeFilter] = useState('all'); // 'all', 'transfer', 'match', 'league'

  // Popups state
  const [counterPopup, setCounterPopup] = useState({ isOpen: false, bidId: null, amount: null, leagueId: null });
  const [actionLoading, setActionLoading] = useState(null);
  const [handledBidIds, setHandledBidIds] = useState(new Set());

  // React Query hooks
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isError,
    refetch
  } = useGetNotifications({
    leagueSlug: selectedLeague !== 'all' ? selectedLeague : undefined,
    read: readFilter === 'unread' ? false : undefined,
    type: typeFilter !== 'all' ? typeFilter : undefined,
    enabled: true
  });

  const { data: unreadData, refetch: refetchUnreadCount } = useGetUnreadCount(
    selectedLeague !== 'all' ? selectedLeague : null
  );

  const markAsReadMutation = useMarkNotificationRead();
  const markAllAsReadMutation = useMarkAllNotificationsRead();
  const clearAllMutation = useClearAllNotifications();
  const deleteNotificationMutation = useDeleteNotification();

  const getNotificationLeagueSlug = useCallback((notification, { allowActiveFallback = true } = {}) => (
    notification?.data?.leagueSlug
    || notification?.leagueSlug
    || (selectedLeague !== 'all' ? selectedLeague : null)
    || (allowActiveFallback ? activeLeague?.slug : null)
    || null
  ), [activeLeague?.slug, selectedLeague]);

  const resolveLeagueIdForSlug = useCallback((leagueSlug, { allowActiveFallback = true } = {}) => {
    if (!leagueSlug) {
      return allowActiveFallback ? activeLeague?._id || null : null;
    }

    if (leagueSlug === activeLeague?.slug && activeLeague?._id) {
      return activeLeague._id;
    }

    const targetLeague = userLeagues.find((league) => league.slug === leagueSlug);
    return targetLeague?._id || null;
  }, [activeLeague?._id, activeLeague?.slug, userLeagues]);

  const resolveLeagueIdForNotification = useCallback((notification, options) => {
    const directLeagueId = notification?.data?.leagueId || notification?.leagueId || notification?.league?._id;
    if (directLeagueId) {
      return directLeagueId;
    }

    return resolveLeagueIdForSlug(getNotificationLeagueSlug(notification, options), options);
  }, [getNotificationLeagueSlug, resolveLeagueIdForSlug]);

  const resolveBidActionLeagueId = useCallback((notification) => (
    resolveLeagueIdForNotification(notification, { allowActiveFallback: false })
  ), [resolveLeagueIdForNotification]);

  const resolveTeamIdForLeague = useCallback((leagueSlug) => {
    if (!leagueSlug) {
      return currentTeamId || null;
    }

    if (leagueSlug === activeLeague?.slug && currentTeamId) {
      return currentTeamId;
    }

    const targetLeague = userLeagues.find((league) => league.slug === leagueSlug);
    if (!targetLeague?._id) {
      return null;
    }

    const targetLeagueId = String(targetLeague._id);
    const matchingTeam = myTeams.find((team) => {
      const teamLeagueId = team?.league?._id || team?.league || team?.leagueId;
      if (teamLeagueId && String(teamLeagueId) === targetLeagueId) {
        return true;
      }

      if (Array.isArray(team?.leagues)) {
        return team.leagues.some((leagueId) => String(leagueId) === targetLeagueId);
      }

      return false;
    });

    return matchingTeam?._id || null;
  }, [activeLeague?.slug, currentTeamId, myTeams, userLeagues]);

  // Flatten paginated notifications
  const notifications = data?.pages?.flatMap(page => page.notifications) || [];
  const totalCount = data?.pages?.[0]?.pagination?.total || 0;
  const unreadCount = unreadData?.count || 0;

  // Action map for bid responses
  const ACTION_MAP = {
    accept_bid: 'accept',
    reject_bid: 'reject',
    counter_bid: 'counter'
  };

  // Handle bid actions
  const handleBidAction = async (bidId, actionKey, notificationId) => {
    const notification = notifications.find(n => n._id === notificationId);
    const notificationLeagueId = resolveBidActionLeagueId(notification);
    if (!notification || !notificationLeagueId) {
      console.warn('Cannot respond to bid notification without league information.', { notificationId, bidId });
      toast.error('Could not respond to this offer. Refresh notifications and try again.');
      return;
    }

    if (actionKey === 'counter_bid') {
      setCounterPopup({
        isOpen: true,
        bidId,
        amount: notification.data?.amount || null,
        leagueId: notificationLeagueId
      });
      return;
    }

    const apiAction = ACTION_MAP[actionKey] || actionKey;
    // Immediately disable buttons — before any async work
    setHandledBidIds(prev => new Set([...prev, String(bidId)]));
    setActionLoading(bidId);

    try {
      await NotificationService.respondToBid(bidId, apiAction, null, notificationLeagueId);

      // Mark as read only after successful bid response
      try {
        await markAsReadMutation.mutateAsync(notificationId);
      } catch (markErr) {
        console.warn('Failed to mark notification as read:', markErr);
      }
      toast.success(apiAction === 'reject' ? 'Offer rejected' : 'Offer accepted');
      refetchUnreadCount();
      refetch();
    } catch (err) {
      console.error('Error responding to bid:', err);
      toast.error(err.response?.data?.error || err.message || 'Could not respond to this offer.');
      setHandledBidIds(prev => {
        const next = new Set(prev);
        next.delete(String(bidId));
        return next;
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Handle navigation actions
  const handleNavigationAction = (action, notificationId) => {
    const notif = notifications.find(n => n._id === notificationId);
    const leagueSlug = notif?.data?.leagueSlug || notif?.leagueSlug || activeLeague?.slug;
    const path = resolveNotificationNavigationPath(action, {
      notification: notif,
      leagueSlug,
      teamId: resolveTeamIdForLeague(leagueSlug)
    });

    if (!path) {
      return;
    }

    // Mark notification as read
    markAsReadMutation.mutate(notificationId);
    navigate(path);
  };

  const handleCounterSuccess = async () => {
    setCounterPopup({ isOpen: false, bidId: null, amount: null, leagueId: null });
    refetch();
    refetchUnreadCount();
  };

  // Dismiss notification
  const dismissNotification = (id) => {
    markAsReadMutation.mutate(id);
  };

  // Mark all as read
  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  // Clear all notifications
  const handleClearAll = () => {
    clearAllMutation.mutate();
  };

  // Get notification icon
  const getNotificationIcon = (item) => {
    if (item.type === 'transfer') {
      switch (item.data?.status) {
        case 'auction_won': return '🎯';
        case 'auction_lost': return '❌';
        case 'player_transferred_elsewhere': return '🚫';
        case 'outbid': return '📈';
        case 'bid_received': return '📥';
        case 'accepted': return '✅';
        case 'rejected': return '🚫';
        case 'completed': return '🤝';
        case 'budget': return '💰';
        case 'free_agent_bid': return '📈';
        case 'counter_received': return '🔄';
        default: return '💸';
      }
    } else if (item.type === 'match') {
      if (item.data?.action === 'league') return '⚽';
      if (item.data?.action === 'result') return '📊';
      return '🏅';
    } else if (item.type === 'league') {
      return '🏆';
    } else if (item.type === 'system') {
      return '⚙️';
    }
    return null;
  };

  // Get notification text
  const getNotificationText = (item) => {
    // Use virtual text if available
    if (item.text) return item.text;

    if (item.type === 'transfer') {
      if (item.data?.status === 'bid_received') {
        return <>You have received a new bid for <strong>{item.data.playerName}</strong> of <strong>€{item.data.amount}</strong> from <strong>{item.data.team}</strong>.</>;
      } else if (item.data?.status === 'outbid') {
        return <>You have been outbid for <strong>{item.data.playerName}</strong> by <strong>{item.data.team}</strong>.</>;
      } else if (item.data?.status === 'free_agent_bid') {
        return <>A new highest bid of <strong>€{item.data.amount}</strong> for <strong>{item.data.playerName}</strong> was placed by <strong>{item.data.team}</strong>.</>;
      } else if (item.data?.status === 'counter_received') {
        return <>You received a counter offer of <strong>€{item.data.amount}</strong> for <strong>{item.data.playerName}</strong> from <strong>{item.data.team}</strong>.</>;
      } else if (item.data?.status === 'auction_won') {
        return <>Congratulations! You won the auction for <strong>{item.data.playerName}</strong>.</>;
      } else if (item.data?.status === 'auction_lost') {
        return <>Unfortunately, you lost the auction for <strong>{item.data.playerName}</strong>.</>;
      } else if (item.data?.status === 'player_transferred_elsewhere') {
        return <>Your bid for <strong>{item.data.playerName}</strong> was cancelled because the player transferred to <strong>{item.data.team || 'another team'}</strong>.</>;
      } else if (item.data?.status === 'accepted') {
        return <>Your offer for <strong>{item.data.playerName}</strong> has been accepted.</>;
      } else if (item.data?.status === 'rejected') {
        return <>Your offer for <strong>{item.data.playerName}</strong> has been rejected.</>;
      } else if (item.data?.status === 'completed') {
        return <>The transfer of <strong>{item.data.playerName}</strong> is complete.</>;
      } else if (item.data?.status === 'budget') {
        return <>Your transfer budget has been boosted by <strong>€{item.data.amount}</strong>.</>;
      } else if (item.data?.status === 'prize_money') {
        return <>Your prize money for this season has been allocated to your transfer budget.</>;
      }
    } else if (item.type === 'match') {
      if (item.data?.action === 'result') {
        return <>Match result: <strong>{item.data?.homeTeam}</strong> {item.data?.homeScore} - {item.data?.awayScore} <strong>{item.data?.awayTeam}</strong></>;
      } else if (item.data?.action === 'league') {
        if (item.data?.status === 'starting') {
          return <>Your match against <strong>{item.data.opponentTeam}</strong> is starting.</>;
        }
        return item.data?.lineupLockEnabled === true
          ? <>Your match vs <strong>{item.data.opponentTeam}</strong> is ready. Lineup changes are locked.</>
          : <>Your match vs <strong>{item.data.opponentTeam}</strong> is ready. Set your lineup before kickoff.</>;
      } else if (item.data?.action === 'upcoming') {
        return item.data?.lineupLockEnabled === true
          ? <>Your match against <strong>{item.data.opponentTeam}</strong> starts in ~15 minutes. Lineup changes are now locked.</>
          : <>Your match against <strong>{item.data.opponentTeam}</strong> starts in ~15 minutes. Set your lineup before kickoff.</>;
      } else if (item.data?.action === 'auto_started') {
        return <>Your match against <strong>{item.data.opponentTeam}</strong> has started.</>;
      } else if (item.data?.action === 'forfeit_lose') {
        return <>You lost the match against <strong>{item.data.opponentTeam}</strong> 0:3 — no lineup was set.</>;
      } else if (item.data?.action === 'forfeit_win') {
        return <>You won 3:0 against <strong>{item.data.opponentTeam}</strong> by default — they didn't set a lineup.</>;
      } else if (item.data?.action === 'double_forfeit_no_teamsheet') {
        return <>Your match against <strong>{item.data.opponentTeam}</strong> ended in a double forfeit — neither team set a valid lineup. Both teams received a loss and 0 points.</>;
      } else if (item.data?.action === 'cancelled_no_teamsheet') {
        return <>Your match against <strong>{item.data.opponentTeam}</strong> was cancelled — neither team set a lineup.</>;
      }
    } else if (item.type === 'league') {
      if (item.data?.action === 'created') {
        return <>You have successfully created the league "<strong>{item.data.leagueName}</strong>".</>;
      } else if (item.data?.action === 'joined') {
        return <>You have joined the league "<strong>{item.data.leagueName}</strong>".</>;
      } else if (item.data?.action === 'fixtures') {
        return <>Fixtures have been generated for <strong>{item.data.leagueName}</strong>.</>;
      }
    } else if (item.type === 'system') {
      return item.data?.message || 'System notification';
    }

    return 'Notification';
  };

  // Render single notification item
  const renderNotificationItem = (item) => {
    const timeDif = timeDelta(Date.now(), item.createdAt);
    const icon = getNotificationIcon(item);
    const text = getNotificationText(item);
    const bidActionLeagueId = item.additionalAction?.type === 'multi'
      ? resolveBidActionLeagueId(item)
      : null;
    const bidActionDisabledReason = !bidActionLeagueId
      ? 'This bid notification is missing league information. Refresh notifications and try again.'
      : undefined;

    return (
      <div key={item._id} className={`notification-inbox-item ${item.read ? 'read' : 'unread'}`}>
        <button
          type="button"
          className="notification-dismiss-btn"
          onClick={() => dismissNotification(item._id)}
          title="Mark as read"
        >
          ×
        </button>
        <div className="notification-icon-container">
          {icon && <span className="notification-icon">{icon}</span>}
        </div>
        <div className="notification-content">
          <div className="notification-text">{text}</div>
          <div className="notification-meta">
            <span className="notification-time">
              {(!timeDif.days && !timeDif.hours && !timeDif.minutes)
                ? 'now'
                : `${timeDif.days ? `${timeDif.days}d ` : ''}${timeDif.hours ? `${timeDif.hours}h ` : ''}${timeDif.minutes ? `${timeDif.minutes}m ` : ''}ago`}
            </span>
            {item.leagueSlug && (
              <span className="notification-league">{item.leagueSlug}</span>
            )}
          </div>

          {/* Action buttons */}
          {item.additionalAction && (
            <div className="notification-actions">
              {item.additionalAction.type === 'button' && (
                <button
                  type="button"
                  className="notification-action-btn"
                  onClick={() => handleNavigationAction(item.additionalAction.action, item._id)}
                >
                  {item.additionalAction.text}
                </button>
              )}

              {item.additionalAction.type === 'multi' && (
                <div className="notification-button-group">
                  {item.additionalAction.actions.map((action, actionIndex) => (
                    <button
                      key={actionIndex}
                      type="button"
                      className={`notification-action-btn ${action.action === 'accept_bid' ? 'accept' : action.action === 'reject_bid' ? 'reject' : 'counter'}`}
                      onClick={() => handleBidAction(item.additionalAction.bidId, action.action, item._id)}
                      disabled={handledBidIds.has(String(item.additionalAction.bidId)) || item.read || !bidActionLeagueId}
                      title={bidActionDisabledReason}
                    >
                      {actionLoading === item.additionalAction.bidId ? '...' : action.text}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="page-notifications-inbox">
      <div className="notifications-inbox-header">
        <h1>Notifications</h1>
        <div className="header-stats">
          <span className="stat-total">{totalCount} Total</span>
          <span className="stat-unread">{unreadCount} Unread</span>
        </div>
      </div>

      <div className="notifications-inbox-filters">
        {/* League filter */}
        <div className="filter-group">
          <label>League:</label>
          <select
            value={selectedLeague}
            onChange={(e) => setSelectedLeague(e.target.value)}
            className="league-filter-select"
          >
            <option value="all">All Leagues</option>
            {userLeagues.map(league => (
              <option key={league._id} value={league.slug}>
                {league.name}
              </option>
            ))}
          </select>
        </div>

        {/* Type filter tabs */}
        <div className="type-filter-tabs">
          <button
            className={`type-tab ${typeFilter === 'all' ? 'active' : ''}`}
            onClick={() => setTypeFilter('all')}
          >
            All
          </button>
          <button
            className={`type-tab ${typeFilter === 'transfer' ? 'active' : ''}`}
            onClick={() => setTypeFilter('transfer')}
          >
            Transfers
          </button>
          <button
            className={`type-tab ${typeFilter === 'match' ? 'active' : ''}`}
            onClick={() => setTypeFilter('match')}
          >
            Matches
          </button>
          <button
            className={`type-tab ${typeFilter === 'league' ? 'active' : ''}`}
            onClick={() => setTypeFilter('league')}
          >
            League
          </button>
        </div>

        {/* Read/Unread filter */}
        <div className="read-filter-tabs">
          <button
            className={`read-tab ${readFilter === 'all' ? 'active' : ''}`}
            onClick={() => setReadFilter('all')}
          >
            All
          </button>
          <button
            className={`read-tab ${readFilter === 'unread' ? 'active' : ''}`}
            onClick={() => setReadFilter('unread')}
          >
            Unread
          </button>
        </div>

        {/* Bulk actions */}
        <div className="bulk-actions">
          <button
            className="bulk-action-btn"
            onClick={handleMarkAllAsRead}
            disabled={unreadCount === 0 || markAllAsReadMutation.isLoading}
            title="Mark all as read"
          >
            Mark All Read
          </button>
          <button
            className="bulk-action-btn danger"
            onClick={handleClearAll}
            disabled={totalCount === 0 || clearAllMutation.isLoading}
            title="Clear all notifications"
          >
            Clear All
          </button>
        </div>
      </div>

      <div className="notifications-inbox-content">
        {isLoading && notifications.length === 0 ? (
          <div className="notifications-loading">Loading notifications...</div>
        ) : isError ? (
          <div className="notifications-error">
            <p>Failed to load notifications</p>
            <button onClick={() => refetch()}>Retry</button>
          </div>
        ) : notifications.length === 0 ? (
          <div className="notifications-empty">
            <div className="empty-icon">📭</div>
            <h3>No notifications yet</h3>
            <p>When you receive match updates, transfer bids, or league news, they'll appear here.</p>
          </div>
        ) : (
          <InfiniteScroll
            dataLength={notifications.length}
            next={fetchNextPage}
            hasMore={hasNextPage}
            loader={<div className="notifications-loading">Loading more...</div>}
            className="notifications-inbox-list"
          >
            {notifications.map(renderNotificationItem)}
          </InfiniteScroll>
        )}
      </div>

      {/* Counter Bid Popup */}
      <CounterBidPopup
        bidId={counterPopup.bidId}
        initialAmount={counterPopup.amount}
        leagueId={counterPopup.leagueId}
        isOpen={counterPopup.isOpen}
        onClose={() => setCounterPopup({ isOpen: false, bidId: null, amount: null, leagueId: null })}
        onSuccess={handleCounterSuccess}
      />
    </div>
  );
}

export default NotificationsInboxPage;
